import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Sun,
  Sunrise,
  Moon,
  Check,
} from "lucide-react";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { Box } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import "./TeamSocialAvailabilityPage.css";
import TopNavBar from "../../components/TopNavBar";

const TeamSocialAvailabilityPage = () => {
  const navigate = useNavigate();

  // STATE
  const [value, setValue] = React.useState(dayjs("2026-01-01"));
  const [message, setMessage] = useState("");
  const [availabilityGrid, setAvailabilityGrid] = useState({});

  // CONSTANTS
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const weekDaysList = ["MON", "TUE", "WED", "THU", "FRI"];
  const weekendList = ["SUN", "SAT"];
  const coreSlots = ["morning", "afternoon", "evening"];

  const timeSlots = [
    {
      id: "morning",
      label: "MORNING",
      sub: "Before 12 noon",
      icon: <Sunrise size={20} />,
    },
    {
      id: "afternoon",
      label: "AFTERNOON",
      sub: "12-5 pm",
      icon: <Sun size={20} />,
    },
    {
      id: "evening",
      label: "EVENING",
      sub: "After 5 pm",
      icon: <Moon size={20} />,
    },
    { id: "full", label: "FULL DAY", sub: "+ Sleepover", icon: null },
  ];

  // LOGIC HELPERS
  const isDayFull = (day) =>
    coreSlots.every((slot) => availabilityGrid[`${day}-${slot}`]);
  const isDayTouched = (day) =>
    coreSlots.some((slot) => availabilityGrid[`${day}-${slot}`]);

  const isAllSelected = days.every((day) => isDayFull(day));
  const areWeekdaysFull = weekDaysList.every((day) => isDayFull(day));
  const isWeekdaysBulkActive =
    areWeekdaysFull && !weekendList.some((day) => isDayTouched(day));
  const areWeekendsFull = weekendList.every((day) => isDayFull(day));
  const isWeekendsBulkActive =
    areWeekendsFull && !weekDaysList.some((day) => isDayTouched(day));

  // HANDLERS
  const toggleCell = (day, slotId) => {
    const newGrid = { ...availabilityGrid };
    if (slotId === "full") {
      const currentlyFull = isDayFull(day);
      coreSlots.forEach((s) => {
        newGrid[`${day}-${s}`] = !currentlyFull;
      });
    } else {
      const key = `${day}-${slotId}`;
      newGrid[key] = !newGrid[key];
    }
    setAvailabilityGrid(newGrid);
  };

  const handleBulkSelect = (type) => {
    let newGrid = { ...availabilityGrid };
    if (type === "all") {
      const targetState = !isAllSelected;
      days.forEach((day) =>
        coreSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
    } else if (type === "weekdays") {
      const targetState = !isWeekdaysBulkActive;
      weekDaysList.forEach((day) =>
        coreSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
      if (targetState)
        weekendList.forEach((day) =>
          coreSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = false)),
        );
    } else if (type === "weekends") {
      const targetState = !isWeekendsBulkActive;
      weekendList.forEach((day) =>
        coreSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
      if (targetState)
        weekDaysList.forEach((day) =>
          coreSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = false)),
        );
    }
    setAvailabilityGrid(newGrid);
  };

  const handleSubmit = () => {
    console.log("Availability Submitted:", {
      date: value,
      grid: availabilityGrid,
      message,
    });
    navigate("/take-the-leash");
  };

  return (
    <div className="tsa-container">
      {/* TOP NAV */}
      <div className="tsa-top-nav">
        <button className="tsa-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span>Back</span>
        </button>

        <div className="tsa-header-icons">
          <TopNavBar theme="light" />
        </div>
      </div>

      {/* CONTENT */}
      <div className="tsa-content">
        <h1 className="tsa-title">DIMSUM'S AVAILABILITY</h1>
        <p className="tsa-subtitle">
          Choose a time to enjoy Solo Time with Dimsum
        </p>

        <div className="tsa-split-layout">
          {/* LEFT COLUMN: Calendar & Message (Top on Mobile) */}
          <div className="tsa-left-col">
            <h3 className="tsa-section-label">Week of 12/20/25</h3>

            {/* Calendar */}
            <div className="tsa-calendar-wrapper">
              <div className="tsa-cal-top-bar">
                <span>
                  {value ? dayjs(value).format("YYYY, MMM D") : "Select a date"}
                </span>
                <span className="tsa-edit-icon">✎</span>
              </div>
              <div className="tsa-wrapper-purple">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ width: "100%" }}>
                    <DateCalendar
                      value={value}
                      onChange={(newValue) => setValue(newValue)}
                    />
                  </Box>
                </LocalizationProvider>
              </div>
            </div>

            <h3 className="tsa-section-label" style={{ marginTop: "1.5rem" }}>
              Your Message
            </h3>
            <textarea
              className="tsa-message-input"
              placeholder="Enter here"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          {/* RIGHT COLUMN: Grid (Bottom on Mobile) */}
          <div className="tsa-right-col">
            <div className="tsa-bulk-select-row" style={{ display: "none" }}>
              <div
                className="tsa-bulk-item"
                onClick={() => handleBulkSelect("all")}
              >
                <span>SELECT ALL</span>
                <div
                  className={`tsa-bulk-checkbox ${
                    isAllSelected ? "checked" : ""
                  }`}
                >
                  {isAllSelected && <Check size={24} />}
                </div>
              </div>
              <div
                className="tsa-bulk-item"
                onClick={() => handleBulkSelect("weekdays")}
              >
                <span>WEEKDAYS</span>
                <div
                  className={`tsa-bulk-checkbox ${
                    isWeekdaysBulkActive ? "checked" : ""
                  }`}
                >
                  {isWeekdaysBulkActive && <Check size={24} />}
                </div>
              </div>
              <div
                className="tsa-bulk-item"
                onClick={() => handleBulkSelect("weekends")}
              >
                <span>WEEKENDS</span>
                <div
                  className={`tsa-bulk-checkbox ${
                    isWeekendsBulkActive ? "checked" : ""
                  }`}
                >
                  {isWeekendsBulkActive && <Check size={24} />}
                </div>
              </div>
            </div>

            {/* Grid Header */}
            <div className="tsa-grid-header-row">
              <div className="tsa-empty-cell"></div>
              {timeSlots.map((slot) => (
                <div key={slot.id} className="tsa-header-slot-label">
                  <span
                    className={
                      slot.id === "full"
                        ? "tsa-slot-title-last"
                        : "tsa-slot-title"
                    }
                  >
                    {slot.label}
                  </span>
                  <span
                    className={
                      slot.id === "full" ? "tsa-slot-sub-last" : "tsa-slot-sub"
                    }
                  >
                    {slot.sub}
                  </span>
                </div>
              ))}
            </div>

            {/* Grid Rows */}
            <div className="tsa-grid-body">
              {days.map((day) => {
                const isFullDay = isDayFull(day);

                return (
                  <div key={day} className="tsa-grid-row">
                    <div className="tsa-day-label">{day}</div>

                    {["morning", "afternoon", "evening"].map((slot) => (
                      <div
                        key={slot}
                        className={`tsa-grid-cell tsa-icon-cell ${
                          availabilityGrid[`${day}-${slot}`]
                            ? `tsa-active-${slot}`
                            : ""
                        }`}
                        onClick={() => toggleCell(day, slot)}
                      >
                        {slot === "morning" && (
                          <Sunrise size={24} className="tsa-cell-icon" />
                        )}
                        {slot === "afternoon" && (
                          <Sun size={24} className="tsa-cell-icon" />
                        )}
                        {slot === "evening" && (
                          <Moon size={24} className="tsa-cell-icon" />
                        )}
                      </div>
                    ))}

                    {/* Full Day Checkbox */}
                    <div
                      className={`tsa-bulk-checkbox ${
                        isFullDay ? "checked" : ""
                      }`}
                      onClick={() => toggleCell(day, "full")}
                      style={{ margin: "0 auto" }}
                    >
                      {isFullDay && <Check size={24} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="tsa-footer-section">
          <button className="tsa-submit-btn" onClick={handleSubmit}>
            SUBMIT
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamSocialAvailabilityPage;
